import { Button } from '@/components/base/button'
import { Icon } from '@/components/common/icons/Icon'
import { cn } from '@/helpers/utils'
import { useUiStore, useUiStoreActions } from '@/stores/ui'

import { panelMap } from '@/helpers/panelMap'
import { TEST_IDS } from '@/helpers/constants'

export const PanelRoom = () => {
  const { closeRoomPanel, resetRoomPanelState, setRoomPanelType } =
    useUiStoreActions()

  const panelType = useUiStore(state => state.roomPanelType)
  const roomPanelOpen = useUiStore(state => state.roomPanelOpen)

  const panelContent = panelType ? panelMap[panelType] : null

  // Use the title override from the store if available
  const title = useUiStore(state => state.roomPanelTitle) || panelContent?.title

  const ContentComponent = panelContent?.component
  const displayBackButton = Boolean(
    panelContent && 'parentPanelType' in panelContent,
  )

  const handleBack = () => {
    if (panelContent && 'parentPanelType' in panelContent) {
      setRoomPanelType(panelContent.parentPanelType)
    }
  }

  const handleTransitionEnd = () => {
    if (!roomPanelOpen) {
      // reset the panel once the panel closing transition is completed
      resetRoomPanelState()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Close the panel when the Escape key is pressed on any element inside the panel
    if (event.key === 'Escape') {
      closeRoomPanel()
    }
  }

  // TODO: handle key events: onEscapeKeyDown, onInteractOutside, onOpenAutoFocus?
  return (
    <div
      // At mobile width, the panel should be full width
      // At small widths, the panel should cover the video (absolute)
      // At large widths, squeeze the video
      aria-labelledby="room-panel-title"
      className={cn(
        'absolute inset-y-0 right-0 z-50 h-full max-w-[360px] overflow-hidden',
        'md:relative md:inset-y-auto md:right-auto',
        'shrink-0 grow-0',
        'motion-reduce:transition-none',
        'transition-[width] duration-300 ease-in-out',
        'data-[state=open]:ml-5 data-[state=open]:w-[344px] data-[state=open]:border-l data-[state=open]:pl-4',
        'data-[state=closed]:w-0 data-[state=closed]:border-0 data-[state=closed]:pl-0',
        'rounded-tl-2xl bg-background py-4 pr-0 text-secondary shadow-lg',
      )}
      data-state={roomPanelOpen ? 'open' : 'closed'}
      data-testid={TEST_IDS.ROOM_PANEL}
      onKeyDown={handleKeyDown}
      onTransitionEnd={handleTransitionEnd}
      role="dialog"
      tabIndex={0} // required for focus/keyboard events and role="dialog"
    >
      <div className="relative flex h-full w-[326px] flex-col gap-y-2">
        <div className="m-0 flex shrink-0 flex-row items-center py-1">
          {displayBackButton && (
            <Button
              variant="icon"
              onClick={handleBack}
              className="m-0 me-2 h-0 p-0"
            >
              <Icon tag="arrow-back" variant="foreground" size="xl" />
              <span className="sr-only">Back</span>
            </Button>
          )}
          <h2 className="text-title text-foreground" id="room-panel-title">
            {title}
          </h2>
        </div>
        <div className="h-full overflow-hidden">
          {ContentComponent ? <ContentComponent /> : null}
        </div>
        <Button
          className="absolute right-0 top-0 translate-y-[2px]"
          onClick={closeRoomPanel}
          size="icon"
          variant="icon"
        >
          <Icon size="xl" tag="close" variant="foreground" />
          <span className="sr-only">Close</span>
        </Button>
      </div>
    </div>
  )
}
